import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function FormPreviewModal({
  isOpen,
  onClose,
  formFields,
  onSave,
  form,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // No need to set form name since we're removing the input field
  }, [form]);

  if (!isOpen) return null;

  const handleSave = async (e) => {
    e.preventDefault();

    setIsSaving(true);
    try {
      const formData = {
        id: form?.id || Date.now().toString(),
        name: form.name, // Use the form name directly from the form prop
        fields: formFields,
        type: form.type, // Ensure form type is included
        createdAt: form?.createdAt ?? new Date().toISOString(),
      };

      console.log("Saving Form", formData);

      // Save to localStorage
      const savedForms = JSON.parse(localStorage.getItem("savedForms") || "[]");
      const updatedForms = savedForms.filter((f) => f.id !== formData.id);
      updatedForms.push(formData);
      localStorage.setItem("savedForms", JSON.stringify(updatedForms));

      if (onSave) onSave(formData);
      onClose();
      navigate("/forms");
    } catch (error) {
      console.error("Error saving form:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case "text":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="text"
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        );

      case "number":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="number"
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        );

      case "textarea":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <textarea
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows={4}
            />
          </div>
        );

      // case 'wysiwyg':
      //   return (
      //     <div className="mb-4">
      //       <label className="block text-sm font-medium text-foreground">
      //         {field.label}
      //         {field.required && <span className="text-red-500 ml-1">*</span>}
      //       </label>
      //       {field.sublabel && (
      //         <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
      //       )}
      //       <div className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm min-h-[100px] bg-muted">
      //         {field.placeholder || 'Rich text editor content will appear here'}
      //       </div>
      //     </div>
      //   );

      case "heading":
        return (
          <div className="mb-4">
            <h2
              className={`
              ${
                field.headingSize === "large"
                  ? "text-2xl"
                  : field.headingSize === "small"
                    ? "text-lg"
                    : "text-xl"
              }
              font-bold text-foreground
            `}
            >
              {field.label || "Heading"}
            </h2>
            {field.sublabel && (
              <p className="mt-1 text-muted-foreground">{field.sublabel}</p>
            )}
          </div>
        );

      case "select":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <select className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
              <option value="">Select an option</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      case "file":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="file"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        );

      case "image":
        return (
          <div className="mb-4">
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <div className={`text-${field.alignment || "left"}`}>
              <div className="inline-block border-2 border-dashed border-input rounded-lg p-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Image preview</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "email":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="email"
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        );

      case "spacer":
        return (
          <div className="mb-4">
            <hr className="border-t border-border" />
          </div>
        );

      case "phone":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="tel"
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              {...(field.required ? { required: true } : {})}
            />
          </div>
        );

      case "radio":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <div className="mt-1 space-y-2">
              {(field.options || []).map((option, index) => (
                <div key={index} className="flex items-center">
                  <input
                    type="radio"
                    id={`${field.id}-${index}`}
                    name={field.id}
                    value={option}
                    className="h-4 w-4 text-blue-600 border-input focus:ring-blue-500"
                    {...(field.required ? { required: true } : {})}
                  />
                  <label
                    htmlFor={`${field.id}-${index}`}
                    className="ml-2 text-sm text-foreground"
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case "checkbox":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <div className="mt-1 space-y-2">
              {(field.options || []).map((option, index) => (
                <div key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${field.id}-${index}`}
                    name={field.id}
                    value={option}
                    className="h-4 w-4 text-blue-600 border-input rounded focus:ring-blue-500"
                    {...(field.required ? { required: true } : {})}
                  />
                  <label
                    htmlFor={`${field.id}-${index}`}
                    className="ml-2 text-sm text-foreground"
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case "date":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.is_required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <input
              type="date"
              placeholder={field.placeholder}
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              {...(field.is_required ? { required: true } : {})}
            />
          </div>
        );

      case "university_repeatable":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.sublabel && (
              <p className="text-sm text-muted-foreground mb-1">{field.sublabel}</p>
            )}
            <div className="mt-1 p-4 border-2 border-dashed border-input rounded-lg bg-muted">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  University Repeatable Field Preview
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Users can add multiple university entries with dropdowns for
                  university name, degree, field of study, graduation year, and
                  GPA
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-muted rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-brand/20">
          <h3 className="text-lg font-semibold text-foreground">Form Preview</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-primary/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSave}>
              {formFields.map((field, index) => (
                <div key={field.id || index}>{renderField(field)}</div>
              ))}

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : "Save Form"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
